/**
 * API Endpoints Configuration
 * Centralized endpoint definitions for the ExamAI application
 * Note: Base URL already includes /api/v1, so endpoints are relative to that
 */

export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // User endpoints
  USERS: {
    BASE: '/users',
    BY_ID: (id) => `/users/${id}`,
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
  },

  // Exam endpoints
  EXAMS: {
    BASE: '/exams',
    BY_ID: (id) => `/exams/${id}`,
    CREATE: '/exams',
    UPDATE: (id) => `/exams/${id}`,
    DELETE: (id) => `/exams/${id}`,
    PUBLISH: (id) => `/exams/${id}/publish`,
    UNPUBLISH: (id) => `/exams/${id}/unpublish`,
    DUPLICATE: (id) => `/exams/${id}/duplicate`,
    QUESTIONS: (id) => `/exams/${id}/questions`,
    STATISTICS: (id) => `/exams/${id}/statistics`,
    AVAILABLE: '/exams/available',
    STUDENT_AVAILABLE: '/exams/student/available',
  },

  // Question endpoints
  QUESTIONS: {
    BASE: '/questions',
    BY_ID: (id) => `/questions/${id}`,
    CREATE: '/questions',
    UPDATE: (id) => `/questions/${id}`,
    DELETE: (id) => `/questions/${id}`,
    BULK_CREATE: '/questions/bulk',
    IMPORT: '/questions/import',
  },

  // Question Sources endpoints
  QUESTION_SOURCES: {
    LIST: '/question-sources',
    QUESTIONS: (sourceId) => `/question-sources/${sourceId}/questions`,
    FILTERS: (sourceId) => `/question-sources/${sourceId}/filters`,
  },

  // Students endpoint
  STUDENTS: {
    LIST: '/students',
  },

  // Exam attempt endpoints
  ATTEMPTS: {
    BASE: '/attempts',
    BY_ID: (id) => `/attempts/${id}`,
    START: (examId) => `/exams/${examId}/start`,
    SUBMIT_ANSWER: (attemptId) => `/attempts/${attemptId}/answer`,
    COMPLETE: (attemptId) => `/attempts/${attemptId}/complete`,
    RESULTS: (attemptId) => `/attempts/${attemptId}/results`,
    MY_ATTEMPTS: '/attempts/my',
  },

  // Class/Course endpoints
  CLASSES: {
    BASE: '/classes',
    BY_ID: (id) => `/classes/${id}`,
    CREATE: '/classes',
    UPDATE: (id) => `/classes/${id}`,
    DELETE: (id) => `/classes/${id}`,
    STUDENTS: (id) => `/classes/${id}/students`,
    ADD_STUDENT: (id) => `/classes/${id}/students`,
    REMOVE_STUDENT: (classId, studentId) => `/classes/${classId}/students/${studentId}`,
    EXAMS: (id) => `/classes/${id}/exams`,
    ASSIGN_EXAM: (id) => `/classes/${id}/exams`,
  },

  // Organization endpoints
  ORGANIZATIONS: {
    BASE: '/organizations',
    BY_ID: (id) => `/organizations/${id}`,
    CREATE: '/organizations',
    UPDATE: (id) => `/organizations/${id}`,
    DELETE: (id) => `/organizations/${id}`,
    MEMBERS: (id) => `/organizations/${id}/members`,
  },

  // Analytics endpoints
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    EXAM_PERFORMANCE: (examId) => `/exams/${examId}/analytics`,
    STUDENT_PROGRESS: (studentId) => `/analytics/students/${studentId}`,
    CLASS_OVERVIEW: (classId) => `/analytics/classes/${classId}`,
    REPORTS: '/analytics/reports',
  },

  // Teacher endpoints
  TEACHER: {
    STUDENT_PERFORMANCE: '/teacher/students/performance',
  },

  // A2A endpoints
  A2A: {
    CAPABILITIES: '/a2a/capabilities',
    EXECUTE: '/a2a/execute',
    STATUS: '/a2a/status',
    HEALTH: '/a2a/health',
  },

  // Grading endpoints
  GRADING: {
    PENDING: '/grading/pending',
    SUBMIT: (attemptId, questionId) => `/grading/${attemptId}/questions/${questionId}`,
    BULK_GRADE: '/grading/bulk',
  },

  // Notification endpoints
  NOTIFICATIONS: {
    BASE: '/notifications',
    MARK_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    PREFERENCES: '/notifications/preferences',
  },

  MATHS_THEOREMS: {
    LIST: '/maths-theorems',
    SAVE_SVG: '/maths-theorems/svg-markup',
  },

  // Health check (absolute path, not relative to base URL)
  HEALTH: '/health',
};

export default API_ENDPOINTS;
